import React, { useEffect, useState, useMemo } from 'react';
import { useLeave } from '../context/LeaveContext';
import { Check, X, User } from 'lucide-react';
import '../styles/MyLeaves.css';

// small date helpers (same formats as MyLeaves)
const pad = (n) => String(n).padStart(2, '0');
const parseDate = (val) => {
     if (!val) return null;
     if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
     const s = String(val).trim();
     if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(s)) return new Date(s.replace(/\s+/, 'T'));
     const dt = new Date(s);
     return Number.isNaN(dt.getTime()) ? null : dt;
};
const formatDateShort = (val) => {
     const dt = parseDate(val);
     if (!dt) return '';
     return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()}`;
};
const formatDateTime = (val) => {
     const dt = parseDate(val);
     if (!dt) return '';
     return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
};

const STATUS_LIST = ['All', 'Pending', 'Approved', 'Rejected', 'Pulled Back'];

const statusClass = (s) => {
     const st = (s || '').toLowerCase();
     if (st.includes('pending')) return 'status-pending';
     if (st.includes('approved') || st.includes('accept')) return 'status-approved';
     if (st.includes('rejected')) return 'status-rejected';
     if (st.includes('pulled') || st.includes('pull')) return 'status-pulled';
     return 'status-unknown';
};

const LeaveRequestsAdmin = () => {
     const { pendingLeaves = [], fetchPendingLeaves, approve, reject, loading } = useLeave();

     // filters + paging (same UX as MyLeaves)
     const [from, setFrom] = useState('');
     const [to, setTo] = useState('');
     const [status, setStatus] = useState('All');
     const [q, setQ] = useState('');
     const [page, setPage] = useState(1);
     const pageSizeOptions = [5, 10, 20];
     const [pageSize, setPageSize] = useState(10);

     useEffect(() => {
          fetchPendingLeaves().catch(() => { });
     }, [fetchPendingLeaves]);

     useEffect(() => { setPage(1); }, [from, to, status, q, pageSize]);

     const filtered = useMemo(() => {
          const f = (leave) => {
               const s = (leave.status || leave.leave_status || '').toString();
               if (status !== 'All' && status && !s.toLowerCase().includes(status.toLowerCase())) return false;

               const start = leave.start_date || leave.from_date || '';
               const end = leave.end_date || leave.to_date || '';
               if (from) {
                    if (!start || new Date(start) < new Date(from)) return false;
               }
               if (to) {
                    if (!end || new Date(end) > new Date(to)) return false;
               }

               if (q) {
                    const ql = q.toLowerCase();
                    const searchable = `${leave.leave_type || ''} ${leave.reason || ''} ${leave.emp_code || ''} ${leave.leave_id || leave.id || ''}`.toLowerCase();
                    if (!searchable.includes(ql)) return false;
               }
               return true;
          };
          return (pendingLeaves || []).filter(f);
     }, [pendingLeaves, from, to, status, q]);

     const total = filtered.length;
     const totalPages = Math.max(1, Math.ceil(total / pageSize));
     const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

     const handleApprove = async (leave) => {
          if (!window.confirm('Approve this leave?')) return;
          try {
               const res = await approve(leave.leave_id || leave.id, { note: 'Approved by admin' });
               if (res && res.status) {
                    window.alert('Approved');
                    fetchPendingLeaves();
               } else {
                    window.alert(res?.message || 'Failed to approve');
               }
          } catch (err) {
               console.error(err);
               window.alert('Error approving leave');
          }
     };

     const handleReject = async (leave) => {
          const reason = window.prompt('Reject reason (optional):', '');
          if (reason === null) return;
          try {
               const res = await reject(leave.leave_id || leave.id, { reason });
               if (res && res.status) {
                    window.alert('Rejected');
                    fetchPendingLeaves();
               } else {
                    window.alert(res?.message || 'Failed to reject');
               }
          } catch (err) {
               console.error(err);
               window.alert('Error rejecting leave');
          }
     };

     const statusClass = (s) => {
          const st = (s || '').toLowerCase();
          if (st.includes('pending')) return 'status-pending';
          if (st.includes('approved') || st.includes('accept')) return 'status-approved';
          if (st.includes('rejected')) return 'status-rejected';
          if (st.includes('pulled') || st.includes('pull')) return 'status-pulled';
          return 'status-unknown';
     };

     // helper: locked statuses should not show actions
     const isLockedStatus = (s) => {
          const st = (s || '').toLowerCase();
          return st.includes('approved') || st.includes('rejected') || st.includes('pulled');
     };

     const exportCsv = (rows) => {
          if (!rows || rows.length === 0) {
               window.alert('No data to export');
               return;
          }
          const cols = ['leave_id', 'employee_name', 'emp_code', 'leave_type', 'from_date', 'to_date', 'days', 'reason', 'applied_on', 'status'];
          const header = ['Leave ID', 'Employee', 'Emp Code', 'Type', 'From', 'To', 'Days', 'Reason', 'Applied On', 'Status'];
          const lines = [header.join(',')];

          rows.forEach(r => {
               const vals = [
                    r.leave_id || r.id || '',
                    r.employee_name || '',
                    (r.emp_code || r.applied_by || ''),
                    (r.leave_type || r.type || ''),
                    (r.start_date || r.from_date || ''),
                    (r.end_date || r.to_date || ''),
                    (r.days || r.total_days || ''),
                    (r.reason || r.remarks || '').replace(/"/g, '""'),
                    (r.applied_on || r.created_at || r.appliedOn || ''),
                    (r.status || r.leave_status || '')
               ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`);
               lines.push(vals.join(','));
          });

          const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `leave_requests_${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
     };

     return (
          <div className="my-leaves-page">
               <div className="card p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                         <h5 className="page-title">Pending Leave Requests</h5>

                         <div className="filters d-flex gap-2 align-items-center">
                              <input type="date" className="form-control form-control-sm" value={from} onChange={e => setFrom(e.target.value)} />
                              <input type="date" className="form-control form-control-sm" value={to} onChange={e => setTo(e.target.value)} />
                              <select value={status} onChange={e => setStatus(e.target.value)} className="form-select form-select-sm">
                                   {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                              <input placeholder="Search" value={q} onChange={e => setQ(e.target.value)} className="form-control form-control-sm" style={{ minWidth: 160 }} />
                              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="form-select form-select-sm">
                                   {pageSizeOptions.map(n => <option key={n} value={n}>{n} / page</option>)}
                              </select>
                         </div>

                         <div className="actions">
                              <button className="btn btn-outline-primary btn-sm" onClick={() => exportCsv(filtered)}>Export CSV</button>
                         </div>
                    </div>

                    <div className="table-responsive compact">
                         <table className="table table-sm mb-0">
                              <thead className="table-light">
                                   <tr>
                                        <th style={{ width: 40 }}>#</th>
                                        <th>Type</th>
                                        <th>Employee</th>
                                        <th>From</th>
                                        <th>To</th>
                                        <th>Days</th>
                                        <th>Reason</th>
                                        <th>Applied On</th>
                                        <th>Status</th>
                                        <th style={{ width: 120 }}>Actions</th>
                                   </tr>
                              </thead>
                              <tbody>
                                   {loading && (
                                        <tr><td colSpan="9" className="text-center">Loading…</td></tr>
                                   )}
                                   {!loading && pageItems.length === 0 && (
                                        <tr><td colSpan="9" className="text-center text-muted">No pending requests</td></tr>
                                   )}
                                   {pageItems.map((l, i) => (
                                        <tr key={l.leave_id || l.id}
                                             className={`leave-row ${statusClass(l.status || l.leave_status || '')}`}>
                                             <td data-label="#" className="col-index">{(page - 1) * pageSize + i + 1}</td>
                                             <td data-label="Type" className="col-type fw-bold">{l.leave_type || l.type || '—'}</td>
                                             <td data-label="Employee" className="col-employee">
                                                  <div style={{ fontWeight: 600 }}>{l.employee_name || l.applied_by || '—'}</div>
                                                  <div className="text-muted small">{l.emp_code || ''} ({l.emp_type || ''})</div>
                                             </td>
                                             <td data-label="From" className="col-from">{(l.start_date || l.from_date) ? formatDateShort(l.start_date || l.from_date) : '—'}</td>
                                             <td data-label="To" className="col-to">{(l.end_date || l.to_date) ? formatDateShort(l.end_date || l.to_date) : '—'}</td>
                                             <td data-label="Days" className="col-days">{l.days || l.total_days || '1'}</td>
                                             <td data-label="Reason" className="col-reason text-truncate" style={{ maxWidth: 240 }}>{l.reason || l.remarks || '-'}</td>
                                             <td data-label="Applied On" className="col-applied">{(l.applied_on || l.created_at || l.appliedOn) ? formatDateTime(l.applied_on || l.created_at || l.appliedOn) : '—'}</td>
                                             <td data-label="Status" className="col-status"><span className={`badge ${statusClass(l.status || l.leave_status || '')}`}>{l.status || l.leave_status || 'Unknown'}</span></td>
                                             <td data-label="Actions" className="col-actions">
                                                  {isLockedStatus(l.status || l.leave_status || '') ? (
                                                       <div className="text-muted small">Locked</div>
                                                  ) : (
                                                       <div className="d-flex gap-1">
                                                            <button
                                                                 className="btn-icon"
                                                                 title="Approve"
                                                                 aria-label="Approve"
                                                                 onClick={() => handleApprove(l)}
                                                                 style={{ color: '#10b981' }}
                                                            >
                                                                 <Check size={16} />
                                                            </button>
                                                            <button
                                                                 className="btn-icon"
                                                                 title="Reject"
                                                                 aria-label="Reject"
                                                                 onClick={() => handleReject(l)}
                                                                 style={{ color: '#ef4444' }}
                                                            >
                                                                 <X size={16} />
                                                            </button>
                                                       </div>
                                                  )}
                                             </td>
                                        </tr>
                                   ))}
                              </tbody>
                         </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2">
                         <div className="text-muted small">Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total}</div>
                         <div className="pagination-compact d-flex align-items-center gap-2">
                              <button className="btn-page" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} aria-label="Previous">‹</button>
                              <div className="page-list d-flex gap-1">
                                   {[...Array(totalPages)].map((_, idx) => {
                                        const pnum = idx + 1;
                                        if (totalPages > 7) {
                                             if (pnum !== 1 && pnum !== totalPages && Math.abs(pnum - page) > 2) {
                                                  if (pnum === 2 && page > 4) return <div key={pnum} className="dots">…</div>;
                                                  return null;
                                             }
                                        }
                                        return (
                                             <button
                                                  key={pnum}
                                                  className={`btn-page ${pnum === page ? 'active' : ''}`}
                                                  onClick={() => setPage(pnum)}
                                                  aria-current={pnum === page ? 'page' : undefined}
                                             >
                                                  {pnum}
                                             </button>
                                        );
                                   })}
                              </div>
                              <button className="btn-page" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} aria-label="Next">›</button>
                         </div>
                    </div>
               </div>
          </div>
     );
};

export default LeaveRequestsAdmin;